import { type NavigateOptions, useNavigate } from '@tanstack/react-router';
import { Fragment, useCallback } from 'react';
import { useCommiteeSlugNavigate } from '@/utils/router';

export type BreadcrumbItem = {
  label: string;
  navigate?: NavigateOptions;
};

export type BreadcrumbProps = {
  items: BreadcrumbItem[];
};

export function useBreadcrumbNavigate() {
  const navigate = useNavigate();
  const commiteeSlugNavigate = useCommiteeSlugNavigate();

  return useCallback(
    (options: NavigateOptions) => {
      const needsCommiteeSlug =
        typeof options.to === 'string' && options.to.includes('$commiteeSlug');
      if (needsCommiteeSlug) commiteeSlugNavigate(options);
      else navigate(options);
    },
    [navigate, commiteeSlugNavigate]
  );
}

export function Breadcrumb({ items = [] }: BreadcrumbProps) {
  const breadcrumbNavigate = useBreadcrumbNavigate();

  return (
    <div className="flex gap-2 text-xs flex-wrap">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;

        if (item.navigate !== undefined) {
          const navigateOption = item.navigate;
          return (
            <Fragment key={`${item.label}-${i}`}>
              <button
                type="button"
                onClick={() => breadcrumbNavigate(navigateOption)}
                className="text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                {item.label}
              </button>
              {!isLast && <span className="text-gray-400">/</span>}
            </Fragment>
          );
        }

        return (
          <Fragment key={`${item.label}-${i}`}>
            <span className="text-gray-700">{item.label}</span>
            {!isLast && <span className="text-gray-400">/</span>}
          </Fragment>
        );
      })}
    </div>
  );
}
